import { AnimatePresence } from 'framer-motion';
import { FC, memo, useEffect, useState } from 'react';

import RedditLogo from '@/assets/icons/reddit-logo.png';
import useAlerts from '@/hooks/useAlerts';
import {
  downloadImagesAsZip,
  downloadSelectedImagesAsZip,
  generateImagesInParallel,
  generateSingleImage,
} from '@/utils/coverImages/imageGeneration';
import { getOptimalFontSize } from '@/utils/coverImages/textDistribution';
import { replaceQuotes } from '@/utils/helpers';

import { ImageVariation } from '.';
import FloatingBox from '../FloatingBox';
import ImageResults from '../ImageResults';
import PostToArticleButton from '../PostToArticleButton';
import Progress from '../Progress';

interface Props {
  variations?: ImageVariation[];
  images?: string[];
  setImages: React.Dispatch<React.SetStateAction<string[] | undefined>>;
  inArticle?: boolean;
  onReset: () => void;
}

const GeneratedImages: FC<Props> = memo(
  ({ variations, images, setImages, inArticle, onReset }) => {
    const alerts = useAlerts();

    const [selectedImages, setSelectedImages] = useState<number[]>([]);
    const [counter, setCounter] = useState(0);
    const [showProgress, setShowProgress] = useState(false);

    const generateImageVariations = async (
      variation: ImageVariation,
    ): Promise<string | undefined> => {
      try {
        return await generateSingleImage(
          () => {
            const imageHolder = document.createElement('div');
            imageHolder.classList.add('image-holder-var37');

            if (variation.divider !== 'none') {
              const middleBreak = document.createElement('div');

              middleBreak.classList.add('break-var37');

              imageHolder.appendChild(middleBreak);
            }

            const rightTextHolder = document.createElement('div');
            rightTextHolder.classList.add('right-text-holder-var37');
            if (variation.reversed) {
              rightTextHolder.classList.add(
                'right-text-holder-var37--reversed',
              );
            }

            const authorRow = document.createElement('div');
            authorRow.classList.add('author-row-var37');

            const authorImage = new Image();
            authorImage.classList.add('author-image-var37');

            const authorName = document.createElement('p');
            authorName.innerText = variation.authorName;
            authorName.classList.add('author-name-var37');

            if (variation.channelNameColor === 'blue') {
              authorName.classList.add('author-name-var37__blue');
            }

            if (variation.channelImageOption === 'reddit') {
              authorImage.src = RedditLogo;
              authorRow.appendChild(authorImage);
            } else {
              if (!!variation.authorImage) {
                authorImage.src = variation.authorImage;
                authorRow.appendChild(authorImage);
              }
            }

            authorRow.appendChild(authorName);
            const leftText = document.createElement('div');
            leftText.classList.add('left-text-var37');

            const leftTextP = document.createElement('p');
            let replacedLeftText = replaceQuotes(variation.leftText);

            const leftAlgorithmResult = getOptimalFontSize(replacedLeftText, {
              maxWidth: 520,
              maxHeight: 560,
              fontFamily: 'Gothic',
              minFontSize: 45,
              maxFontSize: 120,
              lineHeight: '1.2',
            });

            if (variation.leftBrakes) {
              leftTextP.innerText = replacedLeftText;
            } else {
              leftTextP.innerHTML = leftAlgorithmResult.lines.join('<br/>');
            }

            if (variation.leftFontSize) {
              leftText.style.fontSize = `${variation.leftFontSize}px`;
            } else {
              leftText.style.fontSize = `${leftAlgorithmResult.fontSize}px`;
            }

            leftText.appendChild(leftTextP);

            imageHolder.appendChild(leftText);

            const rightText = document.createElement('div');
            rightText.classList.add('right-text-var37');

            const rightTextP = document.createElement('p');

            let text = replaceQuotes(variation.rightText);

            const algorithmResult = getOptimalFontSize(text, {
              maxWidth: 520,
              maxHeight: 430,
              fontFamily: 'Gothic',
              minFontSize: 45,
              maxFontSize: 120,
              lineHeight: '1.2',
            });

            if (variation.brakes) {
              rightTextP.innerText = text;
            } else {
              rightTextP.innerHTML = algorithmResult.lines.join('<br/>');
            }

            if (variation.fontSize) {
              rightText.style.fontSize = `${variation.fontSize}px`;
            } else {
              rightText.style.fontSize = `${algorithmResult.fontSize}px`;
            }

            rightText.appendChild(rightTextP);

            rightTextHolder.appendChild(authorRow);

            rightTextHolder.appendChild(rightText);

            imageHolder.appendChild(rightTextHolder);

            // Temporarily append to DOM to get accurate measurements
            document.body.appendChild(imageHolder);

            const rightTextWidth = rightTextP.offsetWidth;

            const paddingLeft = (534 - rightTextWidth) / 2;

            authorRow.style.paddingLeft =
              paddingLeft < 90 ? `${paddingLeft}px` : '90px';

            // Remove from DOM before returning
            document.body.removeChild(imageHolder);

            return imageHolder;
          },
          {
            holderClassName: 'generated-images-holder-var37',
            pixelRatio: 1,
          },
        );
      } catch (e) {
        alerts.error();
        return undefined;
      }
    };

    const generateAllVariations = async () => {
      if (!variations?.length) return;

      setShowProgress(true);
      setCounter(0);

      try {
        const validImages = await generateImagesInParallel(
          variations,
          generateImageVariations,
          (current) => {
            setCounter(current);
          },
        );

        setImages(validImages);
      } finally {
        setShowProgress(false);
        setCounter(0);
      }
    };

    const handleDownloadZip = async (selected: boolean) => {
      if (!images?.length) return;

      if (selected) {
        await downloadSelectedImagesAsZip(images, selectedImages, {
          filename: 'generated-cover-images.zip',
          imagePrefix: 'image-',
        });
      } else {
        await downloadImagesAsZip(images, {
          filename: 'generated-cover-images.zip',
          imagePrefix: 'image-',
        });
      }
    };

    useEffect(() => {
      generateAllVariations();
      // eslint-disable-next-line
    }, [variations]);

    useEffect(() => {
      if (!images?.length) return;
      setSelectedImages(images?.map((_, index) => index));
    }, [images]);

    return (
      <>
        <ImageResults
          images={images}
          selectedImages={selectedImages}
          setSelectedImages={setSelectedImages}
          setImages={setImages}
        />
        <AnimatePresence>
          {!!showProgress && !!variations?.length && (
            <Progress
              key={1}
              progressPercent={(counter / variations.length) * 100}
            />
          )}
          {!inArticle ? (
            <>
              {!!images?.length && (
                <FloatingBox
                  key={2}
                  selectedImages={selectedImages}
                  unselectAll={() => setSelectedImages([])}
                  selectAll={() =>
                    setSelectedImages(images?.map((_, index) => index))
                  }
                  onDownload={handleDownloadZip}
                  images={images}
                />
              )}
            </>
          ) : (
            <>
              {!!images?.length && (
                <PostToArticleButton
                  images={images}
                  selectedImages={selectedImages}
                  onReset={onReset}
                />
              )}
            </>
          )}
        </AnimatePresence>
      </>
    );
  },
);

export default GeneratedImages;
